export type Office = "no" | "de" | "pl" | "dk";

export type SocialNetwork = "linkedin" | "facebook" | "twitter" | "instagram" | "youtube";

export type SocialLinks = Record<SocialNetwork, string>;

const OFFICE_BY_LOCATION: Record<string, Office> = {
  "berlin": "de",
  "munich": "de",
  "germany": "de",
  "oslo": "no",
  "norway": "no",
  "cracow": "pl",
  "krakow": "pl",
  "kraków": "pl",
  "katowice": "pl",
  "poland": "pl",
  "copenhagen": "dk",
  "københavn": "dk",
  "kobenhavn": "dk",
  "denmark": "dk",
};

export const DEFAULT_OFFICE: Office = "no";

export const SOCIALS: Record<Office, SocialLinks> = {
  no: {
    linkedin: "https://www.linkedin.com/company/forte_digital/",
    facebook: "https://www.facebook.com/fortedigitalno",
    twitter: "https://twitter.com/ForteDigitalNo",
    instagram: "https://www.instagram.com/forte.norge/",
    youtube: "https://www.youtube.com/c/ForteDigital",
  },
  de: {
    linkedin: "https://www.linkedin.com/company/forte-central-europe",
    facebook: "https://www.facebook.com/fortedigitalno/",
    twitter: "https://twitter.com/ForteDigitalNo",
    instagram: "https://www.instagram.com/forte_central_europe/",
    youtube: "https://www.youtube.com/c/ForteDigital",
  },
  pl: {
    linkedin: "https://www.linkedin.com/company/forte-poland",
    facebook: "https://www.facebook.com/fortepoland",
    twitter: "https://twitter.com/ForteDigitalNo",
    instagram: "https://www.instagram.com/forte_poland/",
    youtube: "https://www.youtube.com/@fortepoland",
  },
  dk: {
    linkedin: "https://www.linkedin.com/company/forte-advice",
    facebook: "https://www.facebook.com/forteadvice",
    twitter: "https://twitter.com/ForteDigitalNo",
    instagram: "https://www.instagram.com/forte_advice/",
    youtube: "https://www.youtube.com/c/ForteDigital",
  },
};

export type Website = { url: string; label: string };

export const WEBSITES: Record<Office, Website> = {
  no: { url: "https://www.fortedigital.com/no", label: "fortedigital.com/no" },
  de: { url: "https://www.fortedigital.com/de", label: "fortedigital.com/de" },
  pl: { url: "https://www.fortedigital.com/pl", label: "fortedigital.com/pl" },
  dk: { url: "https://forteadvice.com/", label: "forteadvice.com" },
};

const PLACEHOLDERS: Record<SocialNetwork, string> = {
  linkedin: "LinkedIn",
  facebook: "Facebook",
  twitter: "Twitter",
  instagram: "Instagram",
  youtube: "YouTube",
};

export function getOffice(location?: unknown): Office {
  if (typeof location !== "string") return DEFAULT_OFFICE;

  const normalized = location.trim().toLowerCase();
  if (!normalized) return DEFAULT_OFFICE;

  const exact = OFFICE_BY_LOCATION[normalized];
  if (exact) return exact;


  const partial = Object.keys(OFFICE_BY_LOCATION).find((key) => normalized.includes(key));
  return partial ? OFFICE_BY_LOCATION[partial] : DEFAULT_OFFICE;
}

export function getSocials(location?: unknown): SocialLinks {
  return SOCIALS[getOffice(location)];
}

export function getWebsite(location?: unknown): Website {
  return WEBSITES[getOffice(location)];
}

export function applyWebsite(template: string, website: Website): string {
  return template
    .replaceAll("{WebsiteLabel}", website.label)
    .replaceAll("{Website}", website.url);
}

const FALLBACK_URL = "https://fortedigital.com/";

export function applySocials(template: string, links: SocialLinks): string {
  return (Object.keys(PLACEHOLDERS) as SocialNetwork[]).reduce((html, network) => {
    const name = PLACEHOLDERS[network];
    const url = links[network]?.trim() ?? "";
    return html
      .replaceAll(`{${name}}`, url || FALLBACK_URL)
      .replaceAll(`{${name}On}`, url ? "inline" : "none");
  }, template);
}
